import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, View, TextInput, TouchableOpacity, Image, Platform } from 'react-native';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import BaseText from '../BaseText';
import Icon from '../Icon';
import RowView from '../RowView';
import { scale } from '../../utils/scale.utils';
import { AntDesign } from '@expo/vector-icons';
import { isNullOrEmpty } from '../../utils/common.utils';
import { getCurrencySymbol } from '../../utils/currency.utils';

const useCurrencySymbol = (currencyCode) => {
  return useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);
};

const CustomTextInput = ({
  multiline,
  disabled,
  value,
  onChangeText,
  setFocus,
  enabled,
  fromExpenseFormFields,
  placeholder,
  label,
  isMoneyInput,
  ...rest
}) => {
  return (
    <TextInput
      style={[
        styles.input,
        multiline ? styles.textAreaInput : null,
        disabled && [CommonStyles.disabledFormInput, styles.disabledText],
      ]}
      value={value ? `${value}` : value}
      multiline={multiline}
      onChangeText={(text) => onChangeText(text)}
      onBlur={() => {
        if (!value) {
          setFocus(false);
        }
      }}
      onFocus={() => {
        setFocus(true);
      }}
      placeholder={enabled ? '' : fromExpenseFormFields ? placeholder : label}
      placeholderTextColor={isMoneyInput ? 'black' : 'rgba(0, 0, 0, 0.38)'}
      editable={!disabled}
      {...rest}
    />
  );
};

export const ExpenseFormInput = ({
  label,
  value,
  onChangeText,
  icon,
  isBuildingIcon,
  secondIcon,
  onPressAction,
  image,
  style,
  multiline,
  placeholder,
  // added "fromExpenseFormFields" in order to not affect the behavior for other components which use this one
  fromExpenseFormFields = false,
  isExpenseCar = false,
  disabled = false,
  isMoneyInput = false,
  currencyCode = 'USD',
  isMileageUnit = false,
  companyCurrency = 'USD',
  ...rest
}) => {
  const [focus, setFocus] = useState(false);
  const enabled = focus || value;
  const customTextInputProps = {
    multiline,
    disabled,
    value,
    onChangeText,
    setFocus,
    enabled,
    fromExpenseFormFields,
    placeholder,
    label,
    isMoneyInput,
  };
  const currencyObject = useCurrencySymbol(isMileageUnit ? companyCurrency : currencyCode);
  const currencySymbol = currencyObject?.symbol || '$';

  useEffect(() => {
    if (value) {
      setFocus(true);
    }
  }, [value]);

  return (
    <RowView
      style={[
        styles.item,
        multiline ? null : [styles.textInput, CommonProperties.expenseFieldHeight],
        style,
        disabled && CommonStyles.disabledFormInput,
      ]}
    >
      {icon && isBuildingIcon ? (
        <Icon style={[styles.buildingIcon, multiline ? styles.iconArea : null]} name={icon} />
      ) : null}
      {icon && !isBuildingIcon ? (
        <Icon style={[styles.buildingIcon, multiline ? styles.iconArea : null]} name={icon} />
      ) : null}
      <View
        style={[
          CommonStyles.flex1,
          enabled ? null : CommonStyles.py3,
          multiline ? null : CommonProperties.expenseFieldHeight,
          styles.centerElements,
        ]}
      >
        {!fromExpenseFormFields || enabled || isMoneyInput ? (
          <BaseText style={[styles.title, CommonStyles.h4]}>{label}</BaseText>
        ) : null}
        {isMoneyInput ? (
          <RowView>
            <BaseText style={disabled ? styles.disabledCurrencySymbol : styles.currencySymbol}>
              {currencySymbol}
            </BaseText>
            <CustomTextInput {...customTextInputProps} {...rest} />
          </RowView>
        ) : (
          <CustomTextInput {...customTextInputProps} {...rest} />
        )}
      </View>
      {secondIcon && !image ? (
        <TouchableOpacity onPress={onPressAction}>
          <Icon style={styles.rightIcon} name={secondIcon} />
        </TouchableOpacity>
      ) : null}
      {image ? (
        <TouchableOpacity onPress={onPressAction}>
          <Image style={styles.image} source={{ uri: image }} />
        </TouchableOpacity>
      ) : null}
    </RowView>
  );
};

const ExpenseFormItem = ({
  label,
  value,
  icon,
  secondIcon,
  style,
  onClear,
  isCaretDownIcon = false,
  disabled = false,
}) => {
  return (
    <RowView
      style={[
        styles.item,
        styles.textInput,
        label ? null : CommonStyles.py4,
        style,
        disabled && CommonStyles.disabledFormInput,
        CommonProperties.expenseFieldHeight,
      ]}
    >
      {icon ? <Icon style={styles.icon} name={icon} /> : null}
      <View style={[CommonStyles.flex1, value ? null : CommonStyles.py3]}>
        <BaseText style={[styles.title, value ? CommonStyles.h4 : CommonStyles.h2]}>
          {label}
        </BaseText>
        {!isNullOrEmpty(value) ? (
          <BaseText
            style={disabled ? [styles.disabledText, CommonProperties.h2] : CommonProperties.h2}
            numberOfLines={1}
          >
            {value}
          </BaseText>
        ) : null}
      </View>
      {secondIcon ? <Icon style={styles.rightIcon} name={secondIcon} /> : null}
      {isCaretDownIcon && <AntDesign style={styles.caretDownIcon} name="caretdown" size={18} />}
      {onClear && value ? (
        <TouchableOpacity style={styles.clearButton} onPress={onClear}>
          <Icon color="white" name="cancel" />
        </TouchableOpacity>
      ) : null}
    </RowView>
  );
};

const styles = StyleSheet.create({
  item: {
    ...CommonProperties.px3,
  },
  textInput: {
    alignItems: 'center',
  },
  textAreaInput: {
    minHeight: 120,
  },
  icon: {
    ...CommonProperties.pl1,
    ...CommonProperties.pr4,
    fontSize: scale(28),
    color: Color.grayText2,
  },
  buildingIcon: {
    ...CommonProperties.pl1,
    ...CommonProperties.pr4,
    fontSize: scale(28),
    color: Color.grayText2,
    marginLeft: '-2px',
  },
  iconArea: {
    ...CommonProperties.mt3,
  },
  rightIcon: {
    color: Color.primary,
    fontSize: scale(24),
  },
  title: {
    color: Color.text.low,
  },
  input: {
    // flex: 0,
    width: '98%',
    ...CommonProperties.h2,
    borderWidth: 0,
    outlineColor: Color.none,
    outlineStyle: 'none',
    outlineWidth: 0,
    ...CommonProperties.fontFamily,
    // backgroundColor: 'blue',
  },
  currencySymbol: {
    ...CommonProperties.h2,
    ...CommonProperties.fontFamily,
    marginRight: scale(2),
  },
  disabledCurrencySymbol: {
    ...CommonProperties.h2,
    ...CommonProperties.fontFamily,
    marginRight: scale(2),
    color: Color.disabledText,
  },
  enabledCurrency: {
    color: Color.black,
  },
  disabledText: {
    color: Color.disabledText,
  },
  image: {
    height: 50,
    width: 50,
    borderRadius: 25,
  },
  clearButton: {
    backgroundColor: Color.grayText2,
    height: 20,
    width: 20,
    borderRadius: 20,
    alignSelf: 'flex-end',
    ...CommonProperties.mb2,
    ...CommonProperties.centerAlignment,
  },
  caretDownIcon: {
    alignSelf: 'center',
  },
  fontBlack: {
    color: Color.black,
  },
  centerElements: {
    justifyContent: 'center',
  },
});

export default ExpenseFormItem;
